
import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../../../appContext';
import './SideMenuAdminInfo.css';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { getDailyStats } from '../../../../../api/api';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

interface IDailyStats {
  TotalUsers: number;
  TotalSessions: number;
  TotalClients: number;
  date: string;
}

type DatasetType = 'Users' | 'Sessions' | 'Clients' | 'All';
type TimeFilterType = 'All Time' | 'Last Week' | 'Last Month' | 'Last Year';

export const SideMenuAdminInfo = () => {
    const { translate } = useContext(AppContext);
    const [statsData, setStatsData] = useState<IDailyStats[]>([]);
    const [selectedDatasets, setSelectedDatasets] = useState<DatasetType[]>(['All']);
    const [timeFilter, setTimeFilter] = useState<TimeFilterType>('All Time');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const getFilteredData = () => {
        const now = new Date();
        const data = [...statsData];
        
        switch(timeFilter) {
            case 'Last Week': {
                const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                return fillMissingDates(data.filter(item => new Date(item.date) >= weekAgo), weekAgo, now);
            }
            case 'Last Month': {
                const monthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
                return fillMissingDates(data.filter(item => new Date(item.date) >= monthAgo), monthAgo, now);
            }
            case 'Last Year': {
                const yearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
                return fillMissingDates(data.filter(item => new Date(item.date) >= yearAgo), yearAgo, now);
            }
            default:
                return data;
        }
    };

    const fillMissingDates = (data: IDailyStats[], startDate: Date, endDate: Date) => {
        const dates = new Map<string, IDailyStats>();
        
        // Fill existing data
        data.forEach(item => {
            dates.set(new Date(item.date).toISOString().split('T')[0], item);
        });

        // Fill missing dates with zeros
        const current = new Date(startDate);
        while (current <= endDate) {
            const dateStr = current.toISOString().split('T')[0];
            if (!dates.has(dateStr)) {
                dates.set(dateStr, {
                    TotalUsers: 0,
                    TotalSessions: 0,
                    TotalClients: 0,
                    date: dateStr
                });
            }
            current.setDate(current.getDate() + 1);
        }

        return Array.from(dates.values()).sort((a, b) => 
            new Date(a.date).getTime() - new Date(b.date).getTime()
        );
    };

    useEffect(() => {
        setIsLoading(true);
        setError(null);
        
        getDailyStats()
            .then(data => {
                if (Array.isArray(data)) {
                    const sortedData = data.sort((a: IDailyStats, b: IDailyStats) => 
                        new Date(a.date).getTime() - new Date(b.date).getTime()
                    );
                    setStatsData(sortedData);
                } else {
                    console.warn('Received invalid data format from getDailyStats:', data);
                    setStatsData([]);
                }
            })
            .catch(error => {
                console.error('Failed to fetch daily stats:', error);
                setError('Failed to load analytics data. Please try refreshing the page.');
                setStatsData([]);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    const handleDatasetToggle = (dataset: DatasetType) => {
        if (dataset === 'All') {
            setSelectedDatasets(['All']);
            return;
        }
        
        const newSelection = selectedDatasets.filter(d => d !== 'All');
        if (newSelection.includes(dataset)) {
            setSelectedDatasets(newSelection.filter(d => d !== dataset));
        } else {
            setSelectedDatasets([...newSelection, dataset]);
        }
    };

    const getDatasets = (data: IDailyStats[]) => {
        const datasets = [];
        const showAll = selectedDatasets.includes('All');
        
        if (showAll || selectedDatasets.includes('Users')) {
            datasets.push({
                label: translate('admin_total_users'),
                data: data.map(item => item.TotalUsers),
                borderWidth: 1,
                fill: true,
                backgroundColor: 'rgba(255, 99, 132, 0.2)',
                borderColor: 'rgba(255, 99, 132, 1)'
            });
        }

        if (showAll || selectedDatasets.includes('Sessions')) {
            datasets.push({
                label: translate('admin_total_sessions'),
                data: data.map(item => item.TotalSessions),
                borderWidth: 1,
                fill: true,
                backgroundColor: 'rgba(54, 162, 235, 0.2)',
                borderColor: 'rgba(54, 162, 235, 1)'
            });
        }

        if (showAll || selectedDatasets.includes('Clients')) {
            datasets.push({
                label: translate('admin_total_clients'),
                data: data.map(item => item.TotalClients),
                borderWidth: 1,
                fill: true,
                backgroundColor: 'rgba(75, 192, 192, 0.2)',
                borderColor: 'rgba(75, 192, 192, 1)'
            });
        }

        return datasets;
    };

    const filteredData = getFilteredData();
    const chartData = {
        labels: filteredData.map(item => new Date(item.date).toLocaleDateString()),
        datasets: getDatasets(filteredData)
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
                position: 'top' as const,
            },
            y: {
                beginAtZero: true
            }
        }
    };

    return (
        <div className="SideMenuAdminInfo">
            <div className="SideMenuAdminInfo_stats">
                <div className="SideMenuAdminInfo_stat">
                    <div className="SideMenuAdminInfo_stat_number">
                        {statsData.length > 0 ? statsData[statsData.length - 1].TotalUsers : 0}
                    </div>
                    <div className="SideMenuAdminInfo_stat_label">{translate('admin_total_users')}</div>
                </div>
                <div className="SideMenuAdminInfo_stat">
                    <div className="SideMenuAdminInfo_stat_number">
                        {statsData.length > 0 ? statsData[statsData.length - 1].TotalSessions : 0}
                    </div>
                    <div className="SideMenuAdminInfo_stat_label">{translate('admin_total_sessions')}</div>
                </div>
                <div className="SideMenuAdminInfo_stat">
                    <div className="SideMenuAdminInfo_stat_number">
                        {statsData.length > 0 ? statsData[statsData.length - 1].TotalClients : 0}
                    </div>
                    <div className="SideMenuAdminInfo_stat_label">{translate('admin_total_clients')}</div>
                </div>
            </div>
            <div className="SideMenuAdminInfo_controls">
                <div className="SideMenuAdminInfo_control_group">
                    {(['All', 'Users', 'Sessions', 'Clients'] as DatasetType[]).map((dataset) => (
                    <button 
                        key={dataset}
                        className={`SideMenuAdminInfo_control ${selectedDatasets.includes(dataset) ? 'active' : ''}`}
                        onClick={() => handleDatasetToggle(dataset)}
                    >
                        {translate(`admin_dataset_${dataset.toLowerCase()}`)}
                    </button>
                ))}
                </div>
                <div className="SideMenuAdminInfo_control_group">
                    {(['All Time', 'Last Week', 'Last Month', 'Last Year'] as TimeFilterType[]).map((filter) => (
                        <button 
                            key={filter}
                            className={`SideMenuAdminInfo_control ${timeFilter === filter ? 'active' : ''}`}
                            onClick={() => setTimeFilter(filter)}
                        >
                            {translate(`admin_${filter.toLowerCase().replace(' ', '_')}`)}
                        </button>
                    ))}
                </div>
            </div>
            <div className="SideMenuAdminInfo_chart">
                {isLoading ? (
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        height: '300px',
                        fontSize: '16px',
                        color: '#666'
                    }}>
                        Loading analytics data...
                    </div>
                ) : error ? (
                    <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        height: '300px',
                        fontSize: '16px',
                        color: '#d32f2f',
                        textAlign: 'center'
                    }}>
                        <div style={{ marginBottom: '10px' }}>⚠️ {error}</div>
                        <button 
                            onClick={() => window.location.reload()} 
                            style={{
                                padding: '8px 16px',
                                backgroundColor: '#1976d2',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                        >
                            Refresh Page
                        </button>
                    </div>
                ) : (
                    <Line data={chartData} options={options} />
                )}
            </div>
        </div>
    );
};
